import React, { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { useRive } from '@rive-app/react-webgl2';
import riveFile from '../../../../../assets/logo_loading_animation.riv';
import { EASE_OUT, THINKING_LABEL_SHIMMER_CLASS, THINKING_LABEL_SHIMMER_CSS } from '@/components/ui/motion.ts';

const THINKING_LABEL_DELAY_MS = 3300;
const THINKING_LABEL_ROTATION_MS = 5200;
const THINKING_LABEL_SWAP_MS = 180;
const THINKING_LABEL_TRANSITION = `opacity 180ms ${EASE_OUT}, transform 180ms ${EASE_OUT}`;
const LOADING_LOGO_SIZE = '1.8em';
const THINKING_LABEL_ICON_GAP = '8px';
const THINKING_LABELS = [
	'Thinking',
	'Checking',
	'Reading',
	'Reviewing',
	'Scanning',
	'Searching',
	'Looking',
	'Finding',
	'Assembling',
	'Nailing',
	'Tracing',
	'Clarifying',
	'Verifying',
	'Compiling',
	'Resolving',
] as const;

interface LoadingIndicatorProps {
	isLoading: boolean;
	isChatMode: boolean;
	/**
	 * Live progress label from the backend (e.g. "Checking this property's records…"). Most turns
	 * never send one — the rotating labels below cover that default case. When present, it takes
	 * over immediately: no delay, no rotation, since it's real signal rather than a cosmetic filler.
	 */
	statusLabel?: string | null;
}

const ChatResponseLoadingIndicator: React.FC<LoadingIndicatorProps> = (props) => {
	const { isLoading, isChatMode, statusLabel } = props;
	const [showThinkingLabel, setShowThinkingLabel] = useState(false);
	const [thinkingLabel, setThinkingLabel] = useState<(typeof THINKING_LABELS)[number]>(THINKING_LABELS[0]);
	const [isSwappingThinkingLabel, setIsSwappingThinkingLabel] = useState(false);
	const { RiveComponent } = useRive({
		src: riveFile,
		autoplay: isLoading,
	});

	useEffect(() => {
		if (!isLoading || !isChatMode) {
			setShowThinkingLabel(false);
			setThinkingLabel(THINKING_LABELS[0]);
			setIsSwappingThinkingLabel(false);
			return;
		}

		const timeoutId = setTimeout(() => {
			setShowThinkingLabel(true);
		}, THINKING_LABEL_DELAY_MS);

		return () => clearTimeout(timeoutId);
	}, [isChatMode, isLoading]);

	useEffect(() => {
		if (!showThinkingLabel) return;

		let swapTimeoutId: ReturnType<typeof setTimeout> | undefined;
		const intervalId = setInterval(() => {
			setIsSwappingThinkingLabel(true);
			swapTimeoutId = setTimeout(() => {
				setThinkingLabel((currentLabel) => {
					const nextLabels = THINKING_LABELS.filter((label) => label !== currentLabel);
					return nextLabels[Math.floor(Math.random() * nextLabels.length)];
				});
				setIsSwappingThinkingLabel(false);
			}, THINKING_LABEL_SWAP_MS);
		}, THINKING_LABEL_ROTATION_MS);

		return () => {
			clearInterval(intervalId);
			if (swapTimeoutId) clearTimeout(swapTimeoutId);
		};
	}, [showThinkingLabel]);

	if (isChatMode == false) return null;

	// A real status label bypasses the cosmetic delay/rotation entirely — it's backend signal, not
	// filler, so it shows the instant it arrives and never mid-swap-fades like the rotating set.
	const displayedLabel = statusLabel ?? thinkingLabel;
	const isLabelVisible = !!statusLabel || (showThinkingLabel && !isSwappingThinkingLabel);

	return (
		<Box display="inline-flex" alignItems="center" mt={5} position="relative">
			<style>{THINKING_LABEL_SHIMMER_CSS}</style>
			<Box
				as="span"
				aria-hidden="true"
				className={statusLabel || showThinkingLabel ? THINKING_LABEL_SHIMMER_CLASS : undefined}
				color="fg.muted"
				position="absolute"
				left={`calc(${LOADING_LOGO_SIZE} + ${THINKING_LABEL_ICON_GAP})`}
				top="50%"
				textStyle="body.sm"
				whiteSpace="nowrap"
				userSelect="none"
				opacity={isLabelVisible ? 1 : 0}
				transform={isLabelVisible ? 'translateY(-50%) translateX(0)' : 'translateY(-50%) translateX(-8px)'}
				transition={THINKING_LABEL_TRANSITION}
				pointerEvents="none"
				css={{
					'@media (prefers-reduced-motion: reduce)': {
						transform: 'translateY(-50%)',
						transition: `opacity 150ms ${EASE_OUT}`,
					},
				}}
			>
				{displayedLabel}
			</Box>
			<RiveComponent
				style={{
					width: LOADING_LOGO_SIZE,
					height: LOADING_LOGO_SIZE,
					flexShrink: 0,
					minWidth: '1em',
				}}
			/>
		</Box>
	);
};

export default ChatResponseLoadingIndicator;
